import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 95%;
  max-width: 1000px;
  max-height: 80vh;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #2c3e50;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  
  &:hover {
    color: #34495e;
  }
`;

const ModalBody = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StatTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: #2c3e50;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #3498db;
`;

const VendedorCard = styled.div`
  background-color: ${props => props.$bgColor || '#f8f9fa'};
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const VendedorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const VendedorName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.$textColor || '#2c3e50'};
`;

const VendedorStats = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
  color: ${props => props.$textColor || '#7f8c8d'};
`;

const SectionTitle = styled.h3`
  margin: 1rem 0 0.5rem 0;
  font-size: 1.1rem;
  color: #2c3e50;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
`;

// Modal de detalle por vendedor
const VendedorDetailModal = ({ isOpen, onClose, vendedor, clientes, vendedorColor }) => {
  if (!isOpen) return null;
  
  // Calcular estadísticas por día de visita
  const clientesPorDia = useMemo(() => {
    const dias = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO', 'DOMINGO'];
    const stats = {};
    
    dias.forEach(dia => {
      const clientesDelDia = clientes.filter(c => c.clt_diavisita === dia);
      if (clientesDelDia.length > 0) {
        stats[(dia.trim().toUpperCase())] = clientesDelDia.length;
      }
    });
    
    return stats;
  }, [clientes]);
  
  // Calcular estadísticas por frecuencia
  const clientesPorFrecuencia = useMemo(() => {
    const stats = {
      SEMANAL: 0,
      QUINCENAL: 0,
      MENSUAL: 0
    };
    
    clientes.forEach(cliente => {
      if (cliente.clt_frecuencia) {
        stats[(cliente.clt_frecuencia.trim().toUpperCase())] = (stats[(cliente.clt_frecuencia.trim().toUpperCase())] || 0) + 1;
      }
    });
    
    return stats;
  }, [clientes]);
  
  // Calcular estadísticas de coordenadas
  const estadisticasCoordenadas = useMemo(() => {
    const conCoordenadas = clientes.filter(c => c.clt_lat && c.clt_lon && c.clt_lat !== "" && c.clt_lon !== "").length;
    const sinCoordenadas = clientes.length - conCoordenadas;
    
    return {
      conCoordenadas,
      sinCoordenadas,
      porcentajeConCoordenadas: clientes.length > 0 ? (conCoordenadas / clientes.length * 100).toFixed(1) : 0
    };
  }, [clientes]);
  
  const textColor = getContrastColor(vendedorColor);
  
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <ModalHeader style={{ backgroundColor: vendedorColor }}>
          <ModalTitle style={{ color: textColor }}>Detalle de {vendedor.ven_codigo+" - "+vendedor.ven_nombre}</ModalTitle>
          <CloseButton onClick={onClose} style={{ color: textColor }}>&times;</CloseButton>
        </ModalHeader>
        <ModalBody>
          <StatsContainer>
            <StatCard>
              <StatTitle>Total de clientes</StatTitle>
              <StatValue>{clientes.length}</StatValue>
            </StatCard>
            <StatCard>
              <StatTitle>Clientes con coordenadas</StatTitle>
              <StatValue>{estadisticasCoordenadas.conCoordenadas} ({estadisticasCoordenadas.porcentajeConCoordenadas}%)</StatValue>
            </StatCard>
            <StatCard>
              <StatTitle>Clientes sin coordenadas</StatTitle>
              <StatValue>{estadisticasCoordenadas.sinCoordenadas}</StatValue>
            </StatCard>
          </StatsContainer>
          
          <SectionTitle>Clientes por día de visita</SectionTitle>
          <StatsContainer>
            {Object.entries(clientesPorDia).map(([dia, cantidad]) => (
              <StatCard key={dia}>
                <StatTitle>{dia}</StatTitle>
                <StatValue>{cantidad}</StatValue>
              </StatCard>
            ))}
          </StatsContainer>
          
          <SectionTitle>Clientes por frecuencia</SectionTitle>
          <StatsContainer>
            {Object.entries(clientesPorFrecuencia).map(([frecuencia, cantidad]) => {
              if (cantidad > 0) {
                return (
                  <StatCard key={frecuencia}>
                    <StatTitle>{frecuencia.charAt(0) + frecuencia.slice(1).toLowerCase()}</StatTitle>
                    <StatValue>{cantidad}</StatValue>
                  </StatCard>
                );
              }
              return null;
            })}
          </StatsContainer>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Función para determinar si se debe usar texto claro u oscuro según el color de fondo
const getContrastColor = (hexColor) => {
  // Si no hay color, usar texto oscuro por defecto
  if (!hexColor) return '#2c3e50';
  
  // Convertir hex a RGB
  let r = 0, g = 0, b = 0;
  
  // Verificar formato #RRGGBB
  if (hexColor.startsWith('#') && hexColor.length === 7) {
    r = parseInt(hexColor.substring(1, 3), 16);
    g = parseInt(hexColor.substring(3, 5), 16);
    b = parseInt(hexColor.substring(5, 7), 16);
  }
  
  // Calcular luminosidad
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Usar texto claro para fondos oscuros y viceversa
  return luminance > 0.5 ? '#2c3e50' : '#ffffff';
};

const StatsModal = ({ isOpen, onClose, clientes, vendedores }) => {
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const { getCodigosVendedor } = useAuth?.() || { getCodigosVendedor: () => [] };
  const codigosUsuario = useMemo(() => (typeof getCodigosVendedor === 'function' ? (getCodigosVendedor() || []) : []), [getCodigosVendedor]);
  
  // Si el usuario tiene rutas preestablecidas (códigos de vendedor), filtrar vendedores visibles
  const vendedoresVisibles = useMemo(() => {
    if (Array.isArray(codigosUsuario) && codigosUsuario.length > 0) {
      const setCodigos = new Set(codigosUsuario.map(String));
      return (vendedores || []).filter(v => setCodigos.has(String(v?.ven_codigo)));
    }
    return vendedores || [];
  }, [vendedores, codigosUsuario]);
  
  // Filtrar clientes visibles según los códigos del usuario (para estadísticas generales)
  const clientesVisibles = useMemo(() => {
    if (Array.isArray(codigosUsuario) && codigosUsuario.length > 0) {
      const setCodigos = new Set(codigosUsuario.map(String));
      return (clientes || []).filter(c => setCodigos.has(String(c?.clt_ven_codigo)));
    }
    return clientes || [];
  }, [clientes, codigosUsuario]);
  
  // Calcular estadísticas generales (únicos por clt_codigo)
  const gruposPorCodigo = useMemo(() => {
    const map = new Map();
    const order = [];
    clientesVisibles.forEach((c, idx) => {
      const codigo = c.clt_codigo;
      if (!map.has(codigo)) {
        map.set(codigo, []);
        order.push(codigo);
      }
      map.get(codigo).push({ item: c, index: idx });
    });
    return { map, order };
  }, [clientesVisibles]);

  const totalClientes = gruposPorCodigo.order.length;

  const clientesConCoordenadas = useMemo(() => {
    return gruposPorCodigo.order.reduce((acc, codigo) => {
      const grupo = gruposPorCodigo.map.get(codigo) || [];
      const algunoConCoord = grupo.some(g => g.item?.clt_lat && g.item?.clt_lon && g.item.clt_lat !== "" && g.item.clt_lon !== "");
      return acc + (algunoConCoord ? 1 : 0);
    }, 0);
  }, [gruposPorCodigo]);

  const clientesSinCoordenadas = totalClientes - clientesConCoordenadas;
  const porcentajeConCoordenadas = totalClientes > 0 ? (clientesConCoordenadas / totalClientes * 100).toFixed(1) : 0;
  
  // Agrupar clientes por vendedor (sobre la lista filtrada para el usuario)
  const clientesPorVendedor = vendedoresVisibles.map(vendedor => {
    const clientesDelVendedor = clientes.filter(c => c.clt_ven_codigo === vendedor.ven_codigo);
    const conCoordenadas = clientesDelVendedor.filter(c => c.clt_lat && c.clt_lon && c.clt_lat !== "" && c.clt_lon !== "").length;
    const sinCoordenadas = clientesDelVendedor.length - conCoordenadas;
    
    return {
      vendedor,
      totalClientes: clientesDelVendedor.length,
      clientesConCoordenadas: conCoordenadas,
      clientesSinCoordenadas: sinCoordenadas,
      clientes: clientesDelVendedor
    };
  });
  
  // Manejar clic en vendedor
  const handleVendedorClick = (vendedorData) => {
    setSelectedVendedor(vendedorData);
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Estadísticas de Clientes</ModalTitle>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </ModalHeader>
          <ModalBody>
            <SectionTitle>Estadísticas Generales</SectionTitle>
            <StatsContainer>
              <StatCard>
                <StatTitle>Total de clientes</StatTitle>
                <StatValue>{totalClientes}</StatValue>
              </StatCard>
              <StatCard>
                <StatTitle>Clientes con coordenadas</StatTitle>
                <StatValue>{clientesConCoordenadas} ({porcentajeConCoordenadas}%)</StatValue>
              </StatCard>
              <StatCard>
                <StatTitle>Clientes sin coordenadas</StatTitle>
                <StatValue>{clientesSinCoordenadas}</StatValue>
              </StatCard>
            </StatsContainer>
            
            <SectionTitle>Estadísticas por Vendedor</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {clientesPorVendedor.map((item) => (
                <VendedorCard 
                  key={item.vendedor.ven_codigo}
                  $bgColor={item.vendedor.ven_color}
                  onClick={() => handleVendedorClick(item)}
                >
                  <VendedorInfo>
                    <VendedorName $textColor={getContrastColor(item.vendedor.ven_color)}>
                      {item.vendedor.ven_codigo+" - "+item.vendedor.ven_nombre}
                    </VendedorName>
                    <VendedorStats $textColor={getContrastColor(item.vendedor.ven_color)}>
                      <span>Total: {item.totalClientes}</span>
                      <span>Sin coord.: {item.clientesSinCoordenadas}</span>
                    </VendedorStats>
                  </VendedorInfo>
                  <div style={{ color: getContrastColor(item.vendedor.ven_color) }}>
                    👉 Ver detalle
                  </div>
                </VendedorCard>
              ))}
            </div>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
      
      {selectedVendedor && (
        <VendedorDetailModal
          isOpen={true}
          onClose={() => setSelectedVendedor(null)}
          vendedor={selectedVendedor.vendedor}
          clientes={selectedVendedor.clientes}
          vendedorColor={selectedVendedor.vendedor.ven_color}
        />
      )}
    </>
  );
};

export default StatsModal;
